import { WALL_H_M } from "../geometry/buildingDimensions";
/**
 * Interior-layout geometry constants.
 *
 * These are BUILD dimensions, not admin-tunable thresholds — the validation
 * limits live in the rule registry (`ergonomics.*`, `heater.clearance.*`) so
 * an admin can move them, while the lumber sizes below describe what the
 * model actually draws and what the BOM actually buys.
 *
 * This module is the single owner. `squareBaseBuilder/interior.ts` and
 * `heaterClearanceLayout.ts` import from here rather than re-declaring their
 * own copies — a mirrored constant drifts silently, and the bench geometry
 * has three consumers (renderer, clearance check, BOM) that must agree.
 */

/** Depth of the upper (övre) lav — the seat you sit or lie on. */
export const UPPER_BENCH_DEPTH_MM = 600;

/** Depth of the lower (nedre) foot lav. */
export const LOWER_BENCH_DEPTH_MM = 400;

/** Slat thickness — the visible lav surface, 28×90 kvistfritt. */
export const BENCH_SLAT_THICKNESS_MM = 28;
export const BENCH_SLAT_WIDTH_MM = 90;
/** Drainage/expansion gap between slats. Mirrors `addSlattedBench`'s cap. */
export const BENCH_SLAT_GAP_MM = 18;

/** Perimeter frame and cross joists under the slats: 45×70. */
export const BENCH_FRAME_THICKNESS_MM = 45;
export const BENCH_FRAME_HEIGHT_MM = 70;
/** The wall ledger (bärlina) a wall-hung bench hangs from: 45×90. */
export const BENCH_LEDGER_HEIGHT_MM = 90;

/** Centre-to-centre spacing the cross joists under the slats aim for. */
const BENCH_JOIST_SPACING_MM = 600;
/** Centre-to-centre spacing the front legs aim for. */
const BENCH_LEG_SPACING_MM = 650;
/** Leg cross-section: 70 mm along the run, 45 mm across it. */
export const BENCH_LEG_ALONG_MM = 70;
export const BENCH_LEG_ACROSS_MM = 45;

/** Free height under the upper lav — the foot lav and legs live in it. */
export const UPPER_BENCH_LEG_HEIGHT_MM = 280;
export const UPPER_BENCH_TOP_BELOW_WALL_TOP_M = 0.9;
export const LOWER_BENCH_TOP_BELOW_WALL_TOP_M = 1.35;
/**
 * Seat underside to the finished floor for a lav whose seat centre hangs
 * `topBelowWallTopM` under the wall top. The lavar follow the CEILING, not
 * the floor — heat stratifies, so the seat has to stay the same distance
 * under the ceiling (Finnish/Löyly practice) — which makes every leg a
 * function of the design's wall height (issue 155).
 */
export function benchLegHeightMm(
	wallHeightM: number,
	topBelowWallTopM: number,
): number {
	return Math.round(
		(wallHeightM - topBelowWallTopM) * 1000 - BENCH_SLAT_THICKNESS_MM / 2,
	);
}
/** The lower lav's legs at the DEFAULT wall height. */
export const LOWER_BENCH_LEG_HEIGHT_MM = benchLegHeightMm(
	WALL_H_M,
	LOWER_BENCH_TOP_BELOW_WALL_TOP_M,
);

/**
 * The relax room's bench. Not a lav: it sits at ordinary seat height against a
 * long wall, so none of the sauna tiers' numbers apply to it.
 */
export const RELAX_BENCH_DEPTH_MM = 380;
export const RELAX_BENCH_SEAT_HEIGHT_MM = 450;
/** Total length given up to the two end gaps — half at each end. */
export const RELAX_BENCH_END_MARGIN_MM = 420;
/** Gap between the long wall and the bench's wall-side edge. */
export const RELAX_BENCH_WALL_OFFSET_MM = 70;
/** Post height: seat underside down to the floor. */
export const RELAX_BENCH_LEG_HEIGHT_MM =
	RELAX_BENCH_SEAT_HEIGHT_MM - BENCH_SLAT_THICKNESS_MM / 2;

/** A generated side run stops this far short of the far corner. */
export const BENCH_CORNER_MARGIN_MM = 50;

/**
 * Clearance kept between a clipped bench end and the heater. Shared by the
 * renderer's `clipBenchSpanToHeater` and the layout's `clipRunToHeater`, which
 * must agree about where a bench stops or the BOM buys timber the model never
 * draws.
 */
export const BENCH_HEATER_CLIP_CLEARANCE_MM = 50;

/**
 * Shortest run worth calling a bench. Below this a reconciled run is dropped
 * rather than left as a stub — matching `clipBenchSpanToHeater`, which
 * returns null instead of a 100 mm sliver.
 */
export const MIN_BENCH_RUN_LENGTH_MM = 300;

/**
 * Stainless screws per package, for the fastener BOM row.
 *
 * `prices.rostfriSkruv` is priced **kr/paket**, and `parseQuantity` reads the
 * leading number straight out of the quantity string — so a row that says
 * "143 st" bills 143 packages. The row must be expressed in packages.
 */
export const BENCH_SCREWS_PER_PACK = 200;

/**
 * Number of slats a run of the given depth is built from — one formula shared
 * by the renderer and the BOM, so the model can never draw a slat count the
 * material list did not buy.
 */
export function benchSlatCount(depthMm: number, gapMm?: number): number {
	if (gapMm !== undefined) return Math.max(3, Math.ceil((depthMm + gapMm) / (BENCH_SLAT_WIDTH_MM + gapMm)));
	const pitch = BENCH_SLAT_WIDTH_MM + BENCH_SLAT_GAP_MM;
	return Math.max(3, Math.round((depthMm + BENCH_SLAT_GAP_MM) / pitch));
}

/** Cross joists carrying the slats between the two end rails. */
export function benchJoistCount(lengthMm: number): number {
	return Math.max(1, Math.round(lengthMm / BENCH_JOIST_SPACING_MM) - 1);
}

/**
 * Length of one cross joist. The joists sit BETWEEN the two end rails, so they
 * are a frame thickness shorter at each end than the run's depth — shared by
 * the lpm total and the cut row, which disagreed by 90 mm when they were two
 * separate expressions.
 */
export function benchJoistLengthMm(depthMm: number): number {
	return Math.max(0, depthMm - 2 * BENCH_FRAME_THICKNESS_MM);
}

/** Posts at the room-facing edge of a run that reaches the floor. */
export function benchLegCount(lengthMm: number): number {
	return Math.max(
		2,
		Math.min(4, Math.round(lengthMm / BENCH_LEG_SPACING_MM) + 1),
	);
}

/**
 * Length of the rails that run between the two end rails — the wall ledger and
 * the front board are both inset by one frame thickness at each end.
 */
export function benchRailLengthMm(lengthMm: number): number {
	return Math.max(100, lengthMm - 2 * BENCH_FRAME_THICKNESS_MM);
}
